import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import type { PaidSearchBreakdownType } from '@/api/ad';
import type { TRAFFIC_TYPES } from '@/api/common';
import PaidSearchActivityTableActionMenu from '@/app/paid-search/PaidSearchActivityTableActionMenu';
import PaidSearchAdGroupActivityTableContainer from '@/app/paid-search/PaidSearchAdGroupActivityTableContainer';
import PaidSearchKeywordActivityTableContainer from '@/app/paid-search/PaidSearchKeywordActivityTableContainer';
import { type InstantSearchSortOrder, useInstantSearchState } from '@/components/InstantSearch';
import { usePageFilterContext } from '@/components/page';
import { Panel } from '@/components/panels';
import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs';
import { VisitActivityLegend } from '@/features/VisitActivityLegend';

type Props = {
  breakdownType: PaidSearchBreakdownType;
  defaultSort: {
    sortBy: string;
    sortOrder: InstantSearchSortOrder;
  };
  onScoreAdGroups: (adGroupIds: string[]) => void;
  onScoreKeywords: (keywordIds: string[]) => void;
};

const PaidSearchActivityTableContainer = ({
  defaultSort,
  breakdownType,
  onScoreAdGroups,
  onScoreKeywords,
}: Props) => {
  const {
    pageFilters: { audience },
  } = usePageFilterContext();
  const { queryState, updateQueryState } = useInstantSearchState();
  const [searchParams, setSearchParams] = useSearchParams();
  const [disabledSeries, setDisabledSeries] = useState<TRAFFIC_TYPES[] | undefined>([]);
  const [metricSelectorOpen, setMetricSelectorOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  const breakdownOptionsObject = useMemo<{ value: 'adGroup' | 'keyword'; label: string }[]>(
    () => [
      { value: 'adGroup', label: t`Ad Group` },
      { value: 'keyword', label: t`Keyword` },
    ],
    [],
  );

  const handleBreakdownChange = (value: string | null) => {
    if (value !== 'adGroup' && value !== 'keyword') {
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('type', value);
    setSearchParams(newSearchParams);
    updateQueryState({
      ...queryState,
      sortBy: defaultSort.sortBy,
      sortOrder: defaultSort.sortOrder,
      filters: [],
      page: 1,
    });
  };

  return (
    <Panel
      title={<Trans>Paid Search Reporting</Trans>}
      actions={
        <div className="flex items-center gap-2">
          <Tabs value={breakdownType} onValueChange={handleBreakdownChange}>
            <TabsList data-testid="paid-search-breakdown-type">
              {breakdownOptionsObject.map((option) => (
                <TabsTrigger key={option.value} value={option.value}>
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <PaidSearchActivityTableActionMenu
            onOpenMetricSelector={() => setMetricSelectorOpen(true)}
            onOpenAdvancedFilters={() => setAdvancedFiltersOpen(true)}
          />
        </div>
      }
      isFullHeight
    >
      <VisitActivityLegend audience={audience} onDisabledSeriesChange={setDisabledSeries} />
      {breakdownType === 'adGroup' && (
        <PaidSearchAdGroupActivityTableContainer
          disabledSeries={disabledSeries}
          menuSelectorOpen={metricSelectorOpen}
          advancedFiltersOpen={advancedFiltersOpen}
          onMetricSelectorOpen={setMetricSelectorOpen}
          onAdvancedFiltersOpen={setAdvancedFiltersOpen}
          onScoreAdGroups={onScoreAdGroups}
        />
      )}
      {breakdownType === 'keyword' && (
        <PaidSearchKeywordActivityTableContainer
          disabledSeries={disabledSeries}
          menuSelectorOpen={metricSelectorOpen}
          advancedFiltersOpen={advancedFiltersOpen}
          onMetricSelectorOpen={setMetricSelectorOpen}
          onAdvancedFiltersOpen={setAdvancedFiltersOpen}
          onScoreAds={onScoreKeywords}
        />
      )}
    </Panel>
  );
};

export default PaidSearchActivityTableContainer;
